import {
  scoreQueryOptions,
  scoreRefreshIntervalMs,
} from "../scores/scoreQueryOptions";
/**
 * Reads the team profile and, when the team has a current season, its
 * schedule, and resolves both into one view state the screen renders.
 *
 * The schedule read is gated on a non-null, non-empty `leagueSeasonId`: a
 * team with no current season still renders its header and its tab strip,
 * with no schedule call made at all. The viewer timezone is resolved ONCE
 * here and passed down, so one render pass can never use two zones. This
 * screen does not poll: `refreshAfterSeconds` on the schedule response is
 * deliberately ignored.
 *
 * A failed read is never a missing team. `resolveTeamProfilePhase` in
 * `teamProfileLogic.ts` returns `missing` only for a `NOT_FOUND` answer; any
 * other code, or a thrown read, is `failed` and the screen offers Try again.
 * A failed schedule read keeps the profile ready and sets `isScheduleFailed`,
 * so no panel says the team has no games when the read did not answer
 * (ISSUE-1021).
 */
import { useEventTeamScheduleGet } from "@sportsmet/events-sdk/private";
import { useTeamProfileGet } from "@sportsmet/sports-sdk/private";
import { useEffect } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import type { typeTeamScheduleEvent } from "./teamGameLogic";
import {
  resolveTeamProfilePhase,
  resolveTeamSchedulePhase,
  type typeTeamTabKey,
  visibleTeamTabs,
} from "./teamProfileLogic";

export type typeTeamProfileHeaderData = {
  teamId: string;
  name: string;
  abbreviation: string;
  /** The team's resolved logo URL, or null for the abbreviation plate (ISSUE-1087). */
  logoUrl: string | null;
  recordLabel: string | null;
  standingLabel: string | null;
};

export type typeTeamProfileData =
  | { kind: "loading" }
  | { kind: "missing" }
  | { kind: "failed"; retry: () => void }
  | {
      kind: "ready";
      header: typeTeamProfileHeaderData;
      /**
       * The league this team plays in. Nothing renders it. It is what the Stats
       * panel asks sports-api for, because the only per team statistics the
       * platform stores are the cells of that league's standings row.
       */
      leagueId: string;
      tabs: typeTeamTabKey[];
      games: typeTeamScheduleEvent[];
      /** True when the schedule read answered with an error or threw. `games` is then []. */
      isScheduleFailed: boolean;
      /** Refetches the schedule read only. A no-op when the team has no current season. */
      retrySchedule: () => void;
      timeZone: string;
    };

export function useTeamProfileData(teamId: string): typeTeamProfileData {
  const timeZone = resolveViewerTimeZone();
  const profile = useTeamProfileGet({ teamId });

  const seasonId = profile.data?.data?.leagueSeasonId ?? "";
  const schedule = useEventTeamScheduleGet(
    { teamId, seasonId, timeZone },
    {
      ...scoreQueryOptions,
      enabled: seasonId !== "",
      refetchInterval: (query) =>
        scoreRefreshIntervalMs(query.state.data?.data?.refreshAfterSeconds),
    },
  );

  useEffect(() => {
    const code = extractErrorCode(profile.data);
    if (code !== null)
      showApiError(profile.data, undefined, profile.dataUpdatedAt);
  }, [profile.dataUpdatedAt, teamId]);

  useEffect(() => {
    const code = extractErrorCode(schedule.data);
    if (code !== null && schedule.data?.data == null)
      showApiError(schedule.data, undefined, schedule.dataUpdatedAt);
  }, [schedule.dataUpdatedAt, seasonId, teamId]);

  const profilePhase = resolveTeamProfilePhase({
    isPending: profile.isPending,
    isError: profile.isError,
    errorCode: extractErrorCode(profile.data),
    hasData: profile.data?.data !== null && profile.data?.data !== undefined,
  });
  const schedulePhase = resolveTeamSchedulePhase({
    isEnabled: seasonId !== "",
    isPending: schedule.isPending,
    isError: schedule.isError && schedule.data?.data == null,
    errorCode:
      schedule.data?.data != null ? null : extractErrorCode(schedule.data),
    hasData: schedule.data?.data !== null && schedule.data?.data !== undefined,
  });

  const retry = (): void => {
    // Once the refetched profile carries a season, the schedule query becomes
    // enabled and runs by itself.
    void profile.refetch();
  };

  if (profilePhase === "loading") {
    return { kind: "loading" };
  }
  if (profilePhase === "missing") {
    return { kind: "missing" };
  }
  if (profilePhase === "failed") {
    return { kind: "failed", retry };
  }
  if (schedulePhase === "loading") {
    return { kind: "loading" };
  }

  const data = profile.data?.data;
  if (data === null || data === undefined) {
    return { kind: "failed", retry };
  }

  return {
    kind: "ready",
    header: {
      teamId: data.teamId,
      name: data.name,
      abbreviation: data.abbreviation,
      logoUrl: data.logoUrl ?? null,
      recordLabel: data.recordLabel ?? null,
      standingLabel: data.standingLabel ?? null,
    },
    leagueId: data.leagueId,
    tabs: visibleTeamTabs(data.tabs),
    games: schedulePhase === "ready" ? (schedule.data?.data?.events ?? []) : [],
    isScheduleFailed: schedulePhase === "failed",
    retrySchedule: () => {
      // react-query v5 `refetch` ignores `enabled`, so guard the empty season.
      if (seasonId !== "") void schedule.refetch();
    },
    timeZone,
  };
}
